import { Component } from '@theme/component';

/**
 * A custom element that applies a scroll-driven parallax effect to background media
 * using GSAP ScrollTrigger.
 *
 * The inner media is scaled taller than its container and offset upwards so it can be
 * translated vertically across the scroll range without ever revealing an edge. The
 * translation is driven by a scrubbed ScrollTrigger, which stays in sync with Lenis via
 * the theme's `lenis.on('scroll', ScrollTrigger.update)` wiring.
 *
 * @extends Component
 */
export class ParallaxMedia extends Component {
  /** @type {number} A value between 0 and 1 describing how strong the effect is. */
  #strength = 0.3;

  /** @type {any} The GSAP tween powering the effect. */
  #tween;

  connectedCallback() {
    super.connectedCallback();

    const parsed = Number.parseFloat(this.dataset.parallaxStrength ?? '');
    if (!Number.isNaN(parsed) && parsed > 0) this.#strength = Math.min(parsed, 1);

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    this.#whenReady().then((gsap) => {
      if (gsap && this.isConnected) this.#init(gsap);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#tween?.scrollTrigger?.kill();
    this.#tween?.kill();
    this.#tween = undefined;
  }

  /**
   * Sets up the oversized media and the scrubbed ScrollTrigger tween.
   *
   * @param {any} gsap - The global GSAP instance.
   */
  #init(gsap) {
    const media = this.querySelector('.background-image-container :is(img, svg)');
    if (!(media instanceof HTMLElement)) return;

    gsap.registerPlugin(/** @type {any} */ (window).ScrollTrigger);

    // Ensure this element clips the oversized media and fills the section background.
    this.style.display = 'block';
    this.style.position = 'absolute';
    this.style.inset = '0';
    this.style.overflow = 'hidden';

    const container = media.closest('.background-image-container');
    if (container instanceof HTMLElement) container.style.overflow = 'visible';

    // Oversize the media by `strength * 100%` and shift it up by half of that, so it stays
    // centered at rest and fully covers the container throughout the scroll range.
    const overscanPercent = this.#strength * 100;
    media.style.height = `calc(100% + ${overscanPercent}%)`;
    media.style.top = `-${overscanPercent / 2}%`;
    media.style.position = 'relative';
    media.style.willChange = 'transform';

    // Travel the media from +half to -half of the overscan as the section passes through
    // the viewport. Function-based values keep it correct across resizes.
    const travel = () => (this.offsetHeight * this.#strength) / 2;

    this.#tween = gsap.fromTo(
      media,
      { y: () => travel() },
      {
        y: () => -travel(),
        ease: 'none',
        scrollTrigger: {
          trigger: this,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
          invalidateOnRefresh: true,
        },
      }
    );

  }

  /**
   * Resolves with the global GSAP instance once both GSAP and ScrollTrigger are available,
   * or `undefined` if they never load within a short window.
   *
   * @returns {Promise<any>}
   */
  #whenReady() {
    return new Promise((resolve) => {
      const start = performance.now();

      const check = () => {
        const { gsap, ScrollTrigger } = /** @type {any} */ (window);
        if (gsap && ScrollTrigger) return resolve(gsap);
        if (performance.now() - start > 5000) return resolve(undefined);
        requestAnimationFrame(check);
      };

      check();
    });
  }
}

if (!customElements.get('parallax-media')) {
  customElements.define('parallax-media', ParallaxMedia);
}
